/**
 * DARS Intake Portal — router.
 *
 * Structure mirrors dars-admin-portal:
 *   /login, /forgot-password       → AuthLayout
 *   /                              → MainLayout + nested intake views
 *
 * Every authenticated route is gated on requiredSolution='dars' via
 * router.beforeEach.
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore, useEntitlements } from '@adaptcx/shared'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredSolution: 'dars' },
    children: [
      { path: '', name: 'Dashboard', component: () => import('@/views/DashboardView.vue') },
      { path: 'profile', name: 'ProfileSettings', component: () => import('@/views/profile/ProfileSettingsView.vue') },

      { path: 'packets',           name: 'Packets',           component: () => import('@/views/PacketsView.vue') },
      { path: 'packets/finalized', name: 'PacketFinalized',   component: () => import('@/views/PacketFinalizedView.vue') },
      { path: 'packets/:id',       name: 'PacketDetails',     component: () => import('@/views/PacketDetailsView.vue'), props: true },
      { path: 'packets/:id/edit',  name: 'PacketEdit',        component: () => import('@/views/PacketEditView.vue'), props: true },
      { path: 'packets/:id/link',  name: 'CaseLinking',       component: () => import('@/views/CaseLinkingView.vue'), props: true },
      { path: 'upload',            name: 'PacketUpload',      component: () => import('@/views/PacketUploadView.vue') },
      { path: 'pending',           name: 'PendingReview',     component: () => import('@/views/PendingReviewView.vue') },
      { path: 'incomplete',        name: 'IncompletePackets', component: () => import('@/views/IncompletePacketsView.vue') },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    await authStore.fetchUser()
    if (!authStore.isAuthenticated) {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
  }
  next()
})

router.beforeEach(async (to, _from, next) => {
  const requiredSolution = to.meta.requiredSolution as string | undefined
  if (!requiredSolution) return next()
  const { isSolutionLicensed, loaded, load } = useEntitlements()
  if (!loaded.value) await load()
  if (isSolutionLicensed(requiredSolution)) return next()
  next({ name: 'Login', query: { solutionLocked: requiredSolution } })
})

export default router
